<template>
  <div id="settings-engineers-security-group">
    <div style="display: inline; vertical-align: middle;">
      <span>{{ $t('total') }}: {{ filteredUsers.length }} / {{ recordCounts }}</span>
      <i
        class="fa fa-refresh dark flat icon_refresh"
        @click="refreshData"
      />
    </div>
    <div
      v-if="hasSettingsUpdateAcess"
      class="row controls"
    >
      <HKLButton
        color="darkblue"
        action="resetFilter"
        @click="resetFilter"
      />
      <HKLButton
        v-show="!allowEdit"
        color="orange"
        action="invite"
        @click="showInviteUserModal()"
      />
      <HKLButton
        v-if="!allowEdit"
        color="orange"
        action="edit"
        @click="enableEdit()"
      />
      <HKLButton
        v-if="allowEdit"
        v-hkldreset
        color="darkblue"
        action="cancel"
        @click="discardChange()"
      />
      <HKLButton
        v-if="allowEdit"
        v-hkldreset
        color="orange"
        action="save"
        @click="save()"
      />
    </div>

    <div id="settings-engineers-users-hkland-data">
      <table class="setting-table">
        <thead>
          <th
            v-for="(item, keyName) in defaultHeaders"
            :key="keyName"
            :class="item.class"
            @click="sortingFn(item, keyName)"
          >
            {{ item.name }}
            <HKLSortingIcon
              v-show="userSettingSorting.colName === keyName"
              :iconDirection="sortingDirection"
            />
          </th>
          <th
            v-for="(role, index) in filteredRoles"
            :key="role.id"
          >
            <a
              :id="'settings-engineers-users-hkland-role-' + index"
              @click="navToRoleDetail(role.id)"
            >
              {{ role.name }}
              <i
                class="fa fa-external-link"
                aria-hidden="true"
              />
            </a>
          </th>
          <th style="min-width: 65px;">
            <button
              id="settings-engineers-users-hkland-right-arrow"
              type="button"
              class="btn icon right"
              :disabled="Math.ceil(roles.length / headerPageSize) <= headerPageNum"
              @click="headerPageNum += 1"
            >&#9654;</button>
            <button
              id="settings-engineers-users-hkland-left-arrow"
              type="button"
              class="btn icon right"
              :disabled="headerPageNum <= 1"
              @click="headerPageNum -= 1"
            >&#9664;</button>
          </th>
        </thead>
        <tbody>
          <tr>
            <td class="user-name">
              <HKLInput
                v-model="filterValues.name"
                class="setting-users-filter-search"
              />
            </td>
            <td class="user-status settings-filter">
              <div>
                <HKLSelect
                  v-model="filterValues.enabled"
                  class="align-center"
                  settings-filter-row
                  :options="statusFilterOptions"
                  :showSearch="false"
                  placeholder=""
                />
              </div>
            </td>
            <td
              class="last-login"
            />
            <td
              v-for="role in filteredRoles"
              :key="role.id"
            />
            <td />
          </tr>
          <template v-for="(user, index) in sortedUsers">
            <tr
              :key="user.id"
            >
              <td class="user-name">
                <a
                  :id="'settings-engineers-users-hkland-user-' + index"
                  @click="navToUserDetail(user.id)"
                >
                  {{ user.name }}
                </a>
              </td>
              <td class="user-status">
                <div>{{ user.status }}</div>
              </td>
              <td class="last-login">
                <div>{{ user.lastLoginAt }}</div>
              </td>
              <td
                v-for="(role, roleIndex) in filteredRoles"
                :key="role.id"
              >
                <input
                  v-if="allowEdit || user.checked[role.id]"
                  :id="'settings-engineers-users-hkland-role-checkbox' + roleIndex"
                  v-model="user.checked[role.id]"
                  v-hkldinput
                  type="checkbox"
                  :disabled="!allowEdit || role.disable"
                  @change="updateGrantedAccess(user, role.id, user.checked[role.id])"
                >
              </td>
              <td />
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <InviteUserModal
      ref="inviteUserModal"
    />
  </div>
</template>

<script>
import { computed, reactive, toRefs, getCurrentInstance } from 'vue-demi'
import lodash from 'lodash'

import HKLInput from '@/controls/Input/HKLInput'
import HKLSelect from '@/controls/Select/HKLSelect'
import HKLButton from '@/controls/Button/HKLButton'
import InviteUserModal from './InviteUserModal'
import HKLSortingIcon from '@/controls/Icon/HKLSortingIcon'

import cookie from '@/utils/cookie'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import userHelper from '@/utils/userHelper'
import { aclHelper } from '@/utils/aclHelper'
import { systemUserId, AdminRoleName } from '@/utils/consts'
import { getAllUsersNotInCurrentArea } from '@/api/users/request'
import { getAllUsers, updateUsersRoles } from '@/api/users/users.request'
import { getRoles } from '@/api/roles/request'
import useRegionRef from '@/utils/useRegionRef'
import { unref } from '@vue/composition-api'
import { SORT_DIRECTION } from '@/utils/sortingHelper'

const statusFilterOptions = [
  {
    name: i18n.t('all'),
    value: '',
  },
  {
    name: i18n.t('status.active'),
    value: 1,
  },
  {
    name: i18n.t('status.inactive'),
    value: 0,
  },
]
const defaultHeaders = {
  name: {
    name: i18n.t('settings.hklUser'),
    class: 'pointer',
    filterDefaultValue: '',
    filterFunc: (item, filterValue) => {
      const filterAttributes = [item.username, item.displayName]
      return filterAttributes.some(value => value.toLowerCase().includes(filterValue.toLowerCase()))
    },
  },
  enabled: {
    name: i18n.t('status.name'),
    class: 'user-status pointer',
    filterDefaultValue: lodash.cloneDeep(statusFilterOptions[0].value),
  },
  lastLoginAt: {
    name: i18n.t('lastLogin'),
    class: 'last-login pointer',
    sortingFunc: (item) => new Date(item.lastLogin),
  },
}
const defaultFilterVals = lodash.reduce(defaultHeaders, (rs, item, key) => {
  if (lodash.has(item, 'filterDefaultValue')) rs[key] = item.filterDefaultValue
  return rs
}, {})
const filterValues = useRegionRef({ ...defaultFilterVals }, { logoutReset: true })
const defaultSorting = {
  colName: 'name',
  reverse: false,
}
const userSettingSorting = useRegionRef({ ...defaultSorting }, { logoutReset: true })
const userListCache = useRegionRef([], { logoutReset: true })
const roleListCache = useRegionRef([], { logoutReset: true })
export default {
  components: {
    HKLInput,
    HKLButton,
    HKLSelect,
    HKLSortingIcon,
    InviteUserModal,
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const setupData = reactive({
      sortingDirection: computed(() => {
        return unref(userSettingSorting).reverse ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
      }),
      statusFilterOptions,
      defaultHeaders,
      hasSettingsUpdateAcess: computed(() => {
        return aclHelper.getSettingsUpdateAccess()
      }),
      sortingFn: (item, index) => {
        userSettingSorting.value.colName = index
        userSettingSorting.value.reverse = !unref(userSettingSorting).reverse
      },
      resetFilter: () => {
        filterValues.value = lodash.cloneDeep(defaultFilterVals)
        userSettingSorting.value = lodash.cloneDeep(defaultSorting)
      },
      refreshData: async () => {
        proxy.discardChange()
        await proxy.init()
      },
      recordCounts: computed(() => {
        return userListCache.value.length
      }),
    })
    return {
      roleListCache,
      userListCache,
      userSettingSorting,
      filterValues,
      ...toRefs(setupData),
    }
  },
  data () {
    const headerPageSize = cookie.get('settingColumnSize') || 5

    return {
      allowEdit: false,
      headerPageNum: 1,
      headerPageSize,
      roles: [],
      pendingUpdateUsers: [],
      regionalUserCadidates: [],
    }
  },

  computed: {
    parsedUsers () {
      const result = (this.pendingUpdateUsers || []).filter(item => item.id !== systemUserId)
      return lodash.orderBy(result.map(user => ({
        id: user.id,
        username: user.username,
        displayName: user.displayName,
        name: userHelper.getUserName(user.username, user.displayName),
        enabled: user.enabled ? 1 : 0,
        status: user.enabled ? this.$t('status.active') : this.$t('status.inactive'),
        lastLogin: user.lastLoginAt,
        lastLoginAt: dateUtil.formatDate(user.lastLoginAt, true),
        checked: user.checked,
      })), ['name'], [SORT_DIRECTION.ASC])
    },
    filteredUsers () {
      return this.parsedUsers.filter(item => {
        const matched = lodash.map(this.filterValues, (value, key) => {
          if (value === '') return true // Without inputing filter value
          const defaultFilterFunc = (record, val, attributeName) => (record[attributeName] === val)
          const filterFunc = this.defaultHeaders[key].filterFunc || defaultFilterFunc
          return filterFunc(item, value, key)
        })
        return matched.every(value => value)
      })
    },
    sortedUsers () {
      const direction = this.userSettingSorting.reverse ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
      const colName = this.userSettingSorting.colName
      const sortingFn = this.defaultHeaders[colName].sortingFunc || colName
      const result = lodash.orderBy(this.filteredUsers, [sortingFn], [direction])
      return result
    },
    filteredRoles () {
      return this.roles.filter((role, roleIndex) => Math.ceil((roleIndex + 1) / this.headerPageSize) === this.headerPageNum)
    },
  },

  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },

  async mounted () {
    if (this.userListCache.length) {
      this.useUserListCache()
      this.useRoleListCache()
    } else {
      await this.init()
    }
    window.addEventListener('resize', this.handleResize)
    this.handleResize()

    const { data } = await getAllUsersNotInCurrentArea()
    this.regionalUserCadidates = data
  },

  methods: {
    showInviteUserModal () {
      this.$refs.inviteUserModal.open(this.regionalUserCadidates)
    },
    useRoleListCache () {
      this.roles = lodash.cloneDeep(this.roleListCache)
    },
    async initRoles () {
      const roles = await getRoles()
      this.roleListCache = lodash.orderBy(roles, 'name').map(role => {
        if (role.name === AdminRoleName[0]) {
          role.name = this.$t('settings.security.systemAdmin')
          role.disable = true
        }
        return role
      })
      this.useRoleListCache()
    },
    useUserListCache () {
      this.pendingUpdateUsers = lodash.cloneDeep(this.userListCache)
    },
    async initUsers () {
      const { data: users } = await getAllUsers()
      this.userListCache = users.map(user => {
        user.checked = user.roles.reduce((rs, role) => {
          rs[role.id] = true
          return rs
        }, {})
        return lodash.omit(user, 'roles')
      })
      this.useUserListCache()
    },
    async init () {
      await this.initRoles()
      await this.initUsers()
    },
    enableEdit () {
      this.allowEdit = true
    },
    discardChange () {
      this.useUserListCache()
      this.allowEdit = false
    },

    async save () {
      const users = this.convertUsersWithCheckedRoles()
      await updateUsersRoles(users)
      await this.initUsers()
      this.allowEdit = false
    },

    convertUsersWithCheckedRoles () {
      const updatedUsers = []
      for (let i = 0; i < this.pendingUpdateUsers.length; i += 1) {
        const user = this.pendingUpdateUsers[i]
        const updatedUser = {}
        updatedUser.id = user.id
        const userRoles = []
        const roleIDs = Object.keys(user.checked)
        for (let j = 0; j < roleIDs.length; j += 1) {
          if (roleIDs[j]) {
            userRoles.push(roleIDs[j])
          }
        }
        updatedUser.roles = userRoles
        updatedUsers.push(updatedUser)
      }
      return updatedUsers
    },

    updateGrantedAccess (user, roleId, roleChecked) {
      const roles = user.checked
      if (!roleChecked && roles[roleId] !== undefined) {
        delete roles[roleId]
      }
    },

    navToRoleDetail (id) {
      if (id) {
        this.navPage('security-group-detail', id)
      }
    },
    navToUserDetail (id) {
      if (id) {
        this.navPage('hkl-user-detail', id)
      }
    },

    navPage (name, id) {
      this.$router.push({ name, params: { id } })
    },

    handleResize () {
      // If the pane is not active, you can't get the width of `#settings-engineers-users-hkland-data`
      // Because the element is not visible(display: none)
      const tableWidth = document.getElementById('settings-engineers-users-hkland-data').offsetWidth
      const maxCols = (tableWidth > 1024) ? Math.floor((tableWidth - 615) / 132) : 4
      this.headerPageSize = cookie.get('settingColumnSize') || maxCols
    },
  },

}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';
table {
  border-spacing: 0px;
  .user-name {
    min-width: $settingTableFirstColumnWidth;
    max-width: $settingTableFirstColumnWidth;
  }
  .last-login {
    min-width: 200px;
    max-width: 200px;
  }
  .user-status {
    min-width: 150px;
    max-width: 150px;
  }
}

th:not(:first-child), td:not(:first-child) {
  text-align: center;
}
td {
  padding-top: 2px;
  padding-bottom: 2px;
}
td input[type="text"] {
  width: 150px;
  min-width: 150px;
}

#settings-engineers-users-hkland-user-filter {
  width: 115px;
}
</style>
